import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import { clearSession, customerSession, isAdmin, isCustomer, isLoggedIn, posStaffSession, sessionEmail } from '../api';

// Header account control: "Login" when signed out; otherwise a profile button (initials + name) that
// opens a menu with the right workspace (admin console, partner portal or hangouts) and Logout.
const AccountMenu: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on navigation, outside click and Escape.
  useEffect(() => setOpen(false), [pathname]);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const staff = posStaffSession();
  if (!isLoggedIn() && !isCustomer() && !staff) {
    return <Link className="btn btn-gold nav-login" to="/login">Login</Link>;
  }

  const customer = customerSession();
  const email = sessionEmail();
  const role = staff ? staff.role : isLoggedIn() ? (isAdmin() ? 'Admin' : 'Partner') : 'Guest';
  const name = staff?.name ?? customer?.displayName ?? email?.split('@')[0] ?? role;
  const initials = customer?.initials ?? name.slice(0, 2).toUpperCase();
  const workspace = staff
    ? { to: '/pos', label: 'POS', icon: <PointOfSaleOutlinedIcon /> }
    : role === 'Admin'
    ? { to: '/admin', label: 'Admin Console', icon: <AdminPanelSettingsOutlinedIcon /> }
    : role === 'Partner'
      ? { to: '/partner-account', label: 'Partner Portal', icon: <StorefrontOutlinedIcon /> }
      : { to: '/profile/invites', label: 'My Hangouts', icon: <GroupsOutlinedIcon /> };

  const logout = () => {
    clearSession();
    setOpen(false);
    navigate('/');
  };

  return (
    <div className="account-menu" ref={ref}>
      <button type="button" className="account-btn" onClick={() => setOpen((o) => !o)} aria-haspopup="menu" aria-expanded={open} aria-label={`Account: ${name}`}>
        <span className="account-avatar" aria-hidden="true">{initials}</span>
        <span className="account-name"><b>{name}</b><small>{role}</small></span>
        <KeyboardArrowDownIcon className="account-caret" />
      </button>
      {open && (
        <div className="account-dropdown" role="menu">
          <div className="account-head">
            <b>{name}</b>
            <small>{staff ? `${staff.role} · ${staff.venueName}` : email ?? role}</small>
          </div>
          <Link role="menuitem" to={workspace.to}>{workspace.icon}{workspace.label}</Link>
          {role === 'Partner' && <Link role="menuitem" to="/pos"><PointOfSaleOutlinedIcon />POS &amp; Billing</Link>}
          <button role="menuitem" type="button" className="danger" onClick={logout}><LogoutIcon />Logout</button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
